import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import React, { useMemo } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import useUser from 'shared/hooks/use-user'
import { AudioPlayerAttachment } from 'modules/attachments/components/audio-player'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import AttachmentPreviewItem from 'modules/attachments/components/preview/preview-item'
import { TextWithMentions } from 'modules/community/components/editor/utils/deserialize-mention-to-html'
import { PostPinnedHeader } from 'modules/community/components/pinned-post/components/post-pinned-header'
import {
  UnderModerationPlaceholder,
  shouldHideUnderModerationContent,
} from 'modules/community/components/under-moderation-placeholder'
import { AttachmentTypeEnum } from 'modules/community/types/post-attachment'
import type { PostInterface, SearchPostInterface } from 'modules/community/types/post-interface'
import { isSearchPostGuard } from 'modules/community/types/post-interface'
import { getPrimaryAttachment } from 'modules/community/utils/attachments'

const MAX_CONTENT_TRUNCATED_LENGTH = 125

export interface PostAnnounceLayoutProps<
  T extends PostInterface | SearchPostInterface,
> extends ComponentPropsWithoutRef<'div'> {
  headerTitle: React.ReactNode
  headerActions: React.ReactNode
  footerContent: React.ReactNode
  extraContent?: React.ReactNode
  postData: T
  isPinnedPost?: boolean
  isNeedTruncate?: boolean
}

export const PostAnnounceLayout = <T extends PostInterface | SearchPostInterface>({
  headerTitle,
  headerActions,
  footerContent,
  extraContent,
  postData,
  isPinnedPost,
  isNeedTruncate,
  ...props
}: PostAnnounceLayoutProps<T>): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const hideContent = shouldHideUnderModerationContent(postData.underModeration, user)

  const primaryAttachment = hideContent ? undefined : getPrimaryAttachment(postData.attachments)
  const isSearchPost = isSearchPostGuard(postData)
  const displayContent = isSearchPost
    ? postData.highlight.content || postData.content
    : postData.content

  const displayTitle = useMemo(() => {
    if (isSearchPost && postData.highlight?.title) {
      return DOMPurify.sanitize(postData.highlight.title)
    }
    return DOMPurify.sanitize(postData.title)
  }, [postData.title, isSearchPost && postData.highlight.title])

  return (
    <PostAttachmentsProvider attachments={hideContent ? [] : postData.attachments}>
      {isPinnedPost && <PostPinnedHeader post={postData} className="relative rounded-t-lg" />}
      <div
        className={twJoin(
          `mb-8 w-full cursor-pointer border border-text-secondary/30 bg-bg-primary px-5 py-[25px] font-avertaPE shadow-1 transition-shadow hover:shadow-2`,
          isPinnedPost ? 'rounded-b-lg' : 'rounded-lg',
        )}
        {...props}
      >
        <div className="mb-2 flex justify-between sm:mb-5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5">{headerTitle}</div>
          <div
            className="flex items-center"
            onClick={e => {
              e.stopPropagation()
            }}
          >
            {headerActions}
          </div>
        </div>

        <div className="flex flex-row items-start justify-between gap-2">
          <div className="w-full min-w-0">
            {hideContent ? (
              <UnderModerationPlaceholder type="post" className="pb-1" />
            ) : (
              <>
                <h2
                  className="pb-3.75 community-post-title text-[22px] wrap-break-word"
                  dangerouslySetInnerHTML={{
                    __html: displayTitle,
                  }}
                />
                <TextWithMentions
                  className={twMerge(
                    'preview-image-wrapper cursor-pointer overflow-hidden text-base wrap-break-word text-text-primary/90',
                    isNeedTruncate && 'max-h-[6em]',
                  )}
                  truncateLength={isNeedTruncate ? MAX_CONTENT_TRUNCATED_LENGTH : undefined}
                  text={displayContent}
                  mentions={postData.mentions}
                />
                {!!postData?.attachments?.length && (
                  <>
                    {postData.attachments.map(
                      attachment =>
                        attachment.type === AttachmentTypeEnum.Audio && (
                          <AudioPlayerAttachment
                            attachment={attachment}
                            height="50px"
                            width="100%"
                            className="mt-3"
                            key={attachment.dataFileId}
                          />
                        ),
                    )}
                  </>
                )}
                {isNeedTruncate && (
                  <button
                    data-show-more
                    className="pt-3.75 leading-none text-text-link hover:underline"
                  >
                    {t('post_announce.post_show_more')}
                  </button>
                )}

                {!!primaryAttachment && primaryAttachment.type !== AttachmentTypeEnum.Audio && (
                  <AttachmentPreviewItem
                    className="block size-full max-w-87.5 sm:size-52 md:hidden"
                    attachment={primaryAttachment}
                    key={primaryAttachment.dataFileId}
                  />
                )}
              </>
            )}

            {footerContent}
          </div>

          {!!primaryAttachment && primaryAttachment.type !== AttachmentTypeEnum.Audio && (
            <AttachmentPreviewItem
              className="aspect-auto size-auto shrink-0 max-md:hidden"
              attachment={primaryAttachment}
            />
          )}
        </div>
      </div>
      {extraContent}
    </PostAttachmentsProvider>
  )
}
